import './assets/styles/tailwind.css';

import {Toaster} from '@Toast';
import {useUnleashContext} from '@unleash/proxy-client-react';
import {useEffect} from 'react';
import Routes from 'routes/AppRoutes';
import {getToken, messaging} from 'services/firebase';
import PWAModal from 'shell/PWAModal/PWAModal';
import {useAppDispatch, useAppSelector} from 'store';
import {registerToken} from 'store/slices/notification/thunk';

import {checkPwaStatus, generateUUID, isSessionExpired} from './utils/Helper';
import {sendDataToLighthouse} from './utils/sendDataToLighthouse';

import 'utils/dayjs-jalali-setup';

navigator.serviceWorker.register('/firebase-messaging-sw.js');

type SelectedVendor = {
  id: string | number;
};

const App = () => {
  const {selectedVendor} = useAppSelector((state) => state.dashboard);
  const vendor = selectedVendor as SelectedVendor | undefined;
  const updateContext = useUnleashContext();
  const dispatch = useAppDispatch() as any;
  let sessionTimer: ReturnType<typeof setTimeout>;

  const resetSessionTimer = () => {
    clearTimeout(sessionTimer);
    sessionTimer = setTimeout(() => {
      sessionStorage.clear();
    }, 1800000);
  };

  const handleCheckSession = () => {
    resetSessionTimer();
    const isExpireS = isSessionExpired('userSessionId');
    if (isExpireS) {
      sessionStorage.clear();
      const time = new Date().getTime();
      sessionStorage.setItem('userSessionId', generateUUID(10) + time.toString());
    }
  };

  const handleInstalledApp = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    sendDataToLighthouse({action_name: 'AddToHome'}, userAgent);
  };

  useEffect(() => {
    handleCheckSession();
    window.addEventListener('appinstalled', handleInstalledApp);

    window.addEventListener('mousemove', handleCheckSession);
    window.addEventListener('click', handleCheckSession);
    window.addEventListener('scroll', handleCheckSession);

    return () => {
      window.removeEventListener('appinstalled', handleInstalledApp);
      window.removeEventListener('mousemove', handleCheckSession);
      window.removeEventListener('click', handleCheckSession);
      window.removeEventListener('scroll', handleCheckSession);
    };
  }, []);

  useEffect(() => {
    if (vendor?.id) {
      if ('Notification' in window && typeof Notification.requestPermission === 'function') {
        Notification.requestPermission()
          .then((permission) => {
            if (permission === 'granted') {
              getToken(messaging, {
                vapidKey: import.meta.env.VITE_VAPID_KEY
              })
                .then((currentToken) => {
                  if (currentToken) {
                    console.warn(currentToken);
                    let deviceId: number | null = null;
                    const storedDeviceId = localStorage.getItem('deviceId');
                    deviceId =
                      storedDeviceId !== null &&
                      storedDeviceId !== 'undefined' &&
                      storedDeviceId !== undefined
                        ? Number(storedDeviceId)
                        : null;
                    dispatch(
                      registerToken({
                        vendor_id: vendor.id,
                        token: currentToken,
                        platform: checkPwaStatus() ? 'pwa' : 'web',
                        device_id: deviceId
                      })
                    )
                      .unwrap()
                      .then((res) => {
                        localStorage.setItem('deviceId', String(res.data));
                      });
                  } else {
                    console.warn('No registration token available.');
                  }
                })
                .catch((err) => {
                  console.error('An error occurred while retrieving token.', err);
                });
            } else {
              console.warn('Notification permission not granted:', permission);
            }
          })
          .catch((err) => {
            console.error('Notification.requestPermission() error:', err);
          });
      } else {
        console.warn('Notifications are not supported on this browser/device.');
      }
    }
  }, [vendor?.id, dispatch]);

  useEffect(() => {
    if (vendor?.id) {
      updateContext({
        properties: {vendorId: vendor.id},
        environment: import.meta.env.MODE
      } as Parameters<typeof updateContext>[0]);
    }
  }, [vendor, updateContext]);

  useEffect(() => {
    if (window.clarity) {
      const isPwaUser = checkPwaStatus();
      window.clarity('set', 'PWA_user', isPwaUser ? 'true' : 'false');
    }
  }, []);

  window.addEventListener('appinstalled', () => {
    if (window.clarity) {
      window.clarity('set', 'PWA_installed', 'true');
    }
  });

  return (
    <>
      <PWAModal />
      <Routes />
      <Toaster />
    </>
  );
};

export default App;
